import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, OnDestroy, OnInit, inject } from '@angular/core';
import { LoadingService } from '../../core/shared/loading.service';
import { FormsModule } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { T3SimulationComponent } from './t3-simulation/t3-simulation-panel.component';

import { T3AnalysisRun, T3AnalysisStore } from '../../core/shared/t3-model/t3-analysis.store';
import { T3BaselineService } from '../../core/shared/t3-model/t3-baseline.service';
import { T3Horizon, T3YearStore } from '../../core/shared/t3-model/t3-year.store';
import { LocationSelectionService } from '../service/location-selection.service';

// Use these after you create them.
import { T3StatsComponent } from './t3-stats/t3-stats.component';
import { T3ProjectedImpactComponent } from './t3-projected-impact/t3-projected-impact.component';
import { T3CostmetricsComponent } from './t3-cost-metrics/t3-cost-metrics.component';

type PeriodLabel = '5 years' | '10 years' | '15 years' | '20 years';

@Component({
    selector: 'app-t3-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        T3SimulationComponent,
        T3StatsComponent,
        T3ProjectedImpactComponent,
        T3CostmetricsComponent
    ],
    templateUrl: './t3-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class T3DashboardComponent implements OnInit, OnDestroy {
    private store = inject(T3AnalysisStore);
    private yearStore = inject(T3YearStore);
    private baseline = inject(T3BaselineService);
    private locationSel = inject(LocationSelectionService);
    private destroy$ = new Subject<void>();
    private loadingService = inject(LoadingService);
    private activeBaselineCity = '';
    private baselineLoadInProgress = false;

    runs: T3AnalysisRun[] = [];
    loadingBaseline = true;

    selectedInfoSimulation = 'Basic Simulation';
    selectedInfoPeriod: PeriodLabel = '5 years';
    selectedCascadeSimulation = this.selectedInfoSimulation;

    horizon: T3Horizon = 5;

    async ngOnInit(): Promise<void> {
        this.loadingBaseline = true;
        this.loadingService.show('Loading city data...');

        this.horizon = this.periodToHorizon(this.selectedInfoPeriod);
        this.yearStore.setHorizon(this.horizon);

        // Important: clear stale cached T3 runs immediately.
        // Otherwise old Basic Simulation can render before city dropdown and baseline reload finish.

        combineLatest([this.store.runsList$, this.store.selectedRunId$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([runs, selId]) => {
                this.runs = runs ?? [];

                if (this.baselineLoadInProgress) {
                    this.loadingBaseline = true;
                } else {
                    this.loadingBaseline = this.runs.length === 0;
                }

                if (this.loadingBaseline || !this.runs.length) {
                    return;
                }

                const selectedRunById = selId ? this.runs.find((r) => r.id === selId) : null;

                if (selectedRunById) {
                    this.selectedInfoSimulation = selectedRunById.title;
                    this.selectedCascadeSimulation = selectedRunById.title;
                    return;
                }

                if (!this.runs.some((r) => r.title === this.selectedInfoSimulation)) {
                    const baseline = this.runs.find((r) => (r.title || '').trim().toLowerCase() === 'basic simulation');
                    const fallback = baseline ?? this.runs[0];

                    this.selectedInfoSimulation = fallback?.title ?? 'Basic Simulation';
                    this.selectedCascadeSimulation = this.selectedInfoSimulation;
                }

                const selectedRun = this.runs.find((r) => r.title === this.selectedInfoSimulation);

                if (selectedRun && selectedRun.id !== selId) {
                    this.store.setSelectedRunId(selectedRun.id);
                }
            });

        try {
            const city = await this.waitForSelectedCity('Singapore');
            await this.loadBaselineForCity(city);
        } catch (error) {
            console.error('[T3Dashboard] Failed during initial load', error);
            this.loadingBaseline = true;
        } finally {
            this.loadingService.hide();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSimulationChange(simTitle: string): void {
        this.selectedInfoSimulation = simTitle;
        this.selectedCascadeSimulation = simTitle;

        const run = this.runs.find((r) => r.title === simTitle);
        if (run) {
            this.store.setSelectedRunId(run.id);
        }
    }

    onPeriodChange(label: PeriodLabel): void {
        this.selectedInfoPeriod = label;
        this.horizon = this.periodToHorizon(label);
        this.yearStore.setHorizon(this.horizon);
    }

    trackByRunId = (_: number, r: T3AnalysisRun) => r.id;


    private periodToHorizon(p: PeriodLabel): T3Horizon {
        if (p === '10 years') return 10;
        if (p === '15 years') return 15;
        if (p === '20 years') return 20;
        return 5;
    }

    private async loadBaselineForCity(city: string): Promise<void> {
        const normalizedCity = String(city || '').trim();

        if (!normalizedCity) {
            throw new Error('Missing selected city.');
        }

        this.baselineLoadInProgress = true;
        this.loadingBaseline = true;
        this.loadingService.show('Loading T3 baseline data...');

        try {
            await this.baseline.resetAndReloadBaselineForCity(normalizedCity);

            const runs = this.store.getRunsSnapshot();
            this.runs = runs;
            this.loadingBaseline = runs.length === 0;

            const currentSelectedId = this.store.getSelectedRunId();
            const currentSelectedRun = currentSelectedId
                ? runs.find((r) => r.id === currentSelectedId)
                : null;

            const baselineRun =
                runs.find((r) => (r.title || '').trim().toLowerCase() === 'basic simulation') ??
                runs[0] ??
                null;

            const selected = currentSelectedRun ?? baselineRun;

            if (selected) {
                this.selectedInfoSimulation = selected.title;
                this.selectedCascadeSimulation = selected.title;

                if (selected.id !== currentSelectedId) {
                    this.store.setSelectedRunId(selected.id);
                }
            }
        } catch (error) {
            console.error('[T3Dashboard] Failed to load baseline', error);
            this.loadingBaseline = true;
            throw error;
        } finally {
            this.baselineLoadInProgress = false;
        }
    }
    private async waitForSelectedCity(expectedCity: string, timeoutMs = 15000): Promise<string> {
        const startedAt = Date.now();
        const expected = this.norm(expectedCity);

        while (Date.now() - startedAt < timeoutMs) {
            const selectedCity = String(this.locationSel.getSelected()?.city || '').trim();

            if (selectedCity && this.norm(selectedCity) === expected) {
                return selectedCity;
            }

            this.loadingService.show('Loading city data...');
            await this.sleep(100);
        }

        // Temporary fallback for T3 dashboard because dropdown is locked to Singapore.
        return expectedCity;
    }

    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private norm(value: string): string {
        return String(value || '').trim().toLowerCase();
    }
}